/* ============================================================
   Lab Effects — MOBILE APP SHELL BEHAVIOUR
   assets/le-mobile.js

   Three jobs, all presentation:
     1. collapse the PDP accordions on phones (they ship OPEN)
     2. keep the bottom-tab cart badge in sync
     3. show the "added to cart" toast instead of the cart drawer

   Nothing here renders content. Every word on the page is already in
   the server HTML — see the header comment in le-mobile.css for why
   that matters and why the accordions are rendered open.
   ============================================================ */
(function () {
  'use strict';
  if (window.__leMobileShell) return;
  window.__leMobileShell = true;

  var MOBILE = '(max-width: 767px)';

  /* ---------------------------------------------------------
     0. THERE IS NO VIEWPORT MATHS IN THIS FILE. THAT IS DELIBERATE.

     Both bars are positioned entirely in CSS — the top bar with
     `position: sticky`, the tab bar against `100dvh` (see the long notes in
     le-mobile.css). Nothing here moves them, and nothing here should.

     Two JavaScript approaches have been tried and both were reverted:

       1. --le-pin, a "self-correcting" nudge that did `pin = pin + gap` with
          the gap measured off the bar it had already moved. A feedback loop:
          mid-toolbar-animation the terms are sampled at different instants, so
          it integrated its own error and the bar flew up the page and rolled
          back down.
       2. An absolute correction measured from untouched probe elements, so it
          could not accumulate. The arithmetic was right and it was a verified
          no-op on iOS Safari — but on Chrome for iOS it still had to chase the
          toolbar from scroll events, a frame behind a compositor animation.
          The bars bounced, flickered and lagged on a real device.

     The lesson from both: anything positioning this chrome from JavaScript is
     racing an animation it cannot observe and will always be a frame behind.
     CSS units the compositor already understands are not.

     If bottom- or top-pinned chrome ever looks wrong again, the answer is a
     different declarative expression, not a script — and a real device to test
     it on. Chrome for iOS cannot be installed in the Simulator, which is why
     ?le-vpdebug=1 below exists.
     --------------------------------------------------------- */

  /* ---------------------------------------------------------
     0. ONE OFFSET, APPLIED ONLY WHEN IT SETTLES.

     Chrome for iOS puts the layout viewport's origin ABOVE the visible area.
     Everything positioned against it is therefore displaced upward by the same
     amount, which is exactly what both reported symptoms are:

       • the top bar sticks at layout-top, which is behind Chrome's toolbar —
         you can see the filter row peeking out from under it;
       • the tab bar, positioned from that same origin, lands the same distance
         ABOVE the visible bottom and floats with content beneath it.

     iOS Safari does not do this — measured offsetTop 0 in both toolbar states,
     with a bottom-fixed probe landing exactly on the visible bottom — so the
     correction there is 0 and nothing moves.

     WHY THIS IS NOT THE VERSION THAT BOUNCED.

     The arithmetic here is the same as the attempt that was reverted; the
     update policy is not. That version recomputed and wrote on every scroll
     event, so during Chrome's toolbar animation it was applying a correction
     one frame behind a moving target — the bars chased, flickered and lagged.

     This one SAMPLES during the animation but only WRITES once the number has
     stopped changing (identical on two consecutive frames). So the bars hold
     still while the toolbar moves and land in the right place when it stops.
     The trade is deliberate: a brief moment mid-transition where the bars are
     where they were, instead of continuous chasing. The sampling loop also
     stops on its own rather than running forever.
     --------------------------------------------------------- */
  var vvMq = window.matchMedia('(max-width: 767px)');
  var probeTop = null, probeBot = null;

  function ensureProbes() {
    if (probeTop || !document.body) return;
    var base = 'position:fixed;left:0;width:0;height:0;margin:0;padding:0;' +
               'border:0;pointer-events:none;visibility:hidden;';
    probeTop = document.createElement('div');
    probeTop.setAttribute('aria-hidden', 'true');
    probeTop.style.cssText = base + 'top:0;';
    probeBot = document.createElement('div');
    probeBot.setAttribute('aria-hidden', 'true');
    probeBot.style.cssText = base + 'bottom:0;';
    /* visibility:hidden, not display:none — a display:none box is not laid out
       and its rect would read all zeros, which is the measurement we need. */
    document.body.appendChild(probeTop);
    document.body.appendChild(probeBot);
  }

  /* Measured from probes rather than from the bars themselves. The probes never
     carry the correction, so feeding the same viewport state in twice gives the
     same answer — it cannot accumulate the way the old --le-pin did. */
  function measure() {
    var vv = window.visualViewport;
    if (!vv || !vvMq.matches) return { t: 0, b: 0 };
    ensureProbes();
    if (!probeTop || !probeBot) return null;
    var t = Math.round(vv.offsetTop - probeTop.getBoundingClientRect().top);
    var b = Math.round((vv.offsetTop + vv.height) - probeBot.getBoundingClientRect().bottom);
    /* A browser toolbar is realistically under ~150px. The old ceiling was
       300, which was loose enough to let a mid-gesture reading through — and a
       correction that large is what threw the tab bar into the middle of the
       screen. Anything past this is a transient (pinch-zoom, rotation,
       keyboard) and the frame is discarded rather than acted on. */
    if (Math.abs(t) > 150 || Math.abs(b) > 150) return null;
    return { t: t, b: b };
  }

  var appliedT = 0, appliedB = 0;
  var lastT = null, lastB = null, stable = 0, settleRaf = null, frames = 0;

  /* ONLY the bottom value is published. The top bar is `position: sticky;
     top: 0` and nothing here may move it — a JS-driven `top` on that bar was
     exactly the "flopping and flipping" reported from Chrome for iOS. appliedT
     is still measured, because the debug readout reports it and because a
     non-zero reading there is the evidence needed if the top bar ever does
     need attention. It is not applied. */
  function commit(t, b) {
    appliedT = t;
    if (b === appliedB) return;
    appliedB = b;
    document.documentElement.style.setProperty('--le-vv-bot', b + 'px');
  }

  function settleStep() {
    settleRaf = null;
    var m = measure();
    if (m) {
      if (lastT === m.t && lastB === m.b) {
        stable++;
        // Three identical frames, not two. Chrome's toolbar animation can hold
        // the same value for a frame mid-flight; three means it has stopped.
        if (stable >= 3) { commit(m.t, m.b); lastT = lastB = null; stable = 0; frames = 0; return; }
      } else {
        lastT = m.t; lastB = m.b; stable = 0;
      }
    }
    // Give up rather than spin forever if the viewport never stops moving.
    if (++frames > 45) { if (m) commit(m.t, m.b); lastT = lastB = null; stable = 0; frames = 0; return; }
    settleRaf = requestAnimationFrame(settleStep);
  }

  function kickSettle() {
    if (settleRaf) return;               // a settle pass is already running
    frames = 0; stable = 0; lastT = lastB = null;
    settleRaf = requestAnimationFrame(settleStep);
  }

  if (window.visualViewport) {
    kickSettle();
    window.visualViewport.addEventListener('resize', kickSettle);
    window.visualViewport.addEventListener('scroll', kickSettle);
    window.addEventListener('orientationchange', kickSettle);
    window.addEventListener('pageshow', kickSettle);
    window.addEventListener('resize', kickSettle);
    window.addEventListener('load', kickSettle);
    /* Chrome for iOS moves its toolbar during an ordinary page scroll and does
       not reliably fire a visualViewport event for it, so page scroll has to
       start a settle pass too. It only STARTS one — the pass is already
       running for the rest of the gesture, so this does not add work per
       scroll event the way the reverted version did. */
    window.addEventListener('scroll', kickSettle, { passive: true });
  }

  /* ---------------------------------------------------------
     VIEWPORT DEBUG READOUT — off unless ?le-vpdebug=1 is in the URL.

     None of this runs, or is even reachable, for a shopper. It exists because
     the two browsers disagree about fixed positioning and only one of them can
     be driven from here: iOS Safari runs in the Simulator, Chrome on iOS does
     not exist there and cannot be installed. When Chrome misbehaves the only
     way to see the actual numbers is to read them off a real device, and
     guessing at them is what produced two wrong fixes already.

     Add ?le-vpdebug=1 to any page on a phone and the live values print in the
     corner: layout viewport, visual viewport, both probe rects, and the two
     corrections being applied. Screenshot it and the diagnosis is arithmetic
     rather than inference.
     --------------------------------------------------------- */
  (function debugReadout() {
    if (window.location.search.indexOf('le-vpdebug=1') === -1) return;
    var mkProbes = ensureProbes;   // the shared probes above
    var box = document.createElement('pre');
    box.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:2147483647;' +
      'margin:0;padding:6px 8px;background:rgba(0,0,0,.86);color:#0f0;' +
      'font:10px/1.35 ui-monospace,Menlo,monospace;white-space:pre;pointer-events:none;';
    function draw() {
      var vv = window.visualViewport;
      if (!vv) { box.textContent = 'no visualViewport'; return; }
      mkProbes();
      var pt = probeTop ? probeTop.getBoundingClientRect().top : NaN;
      var pb = probeBot ? probeBot.getBoundingClientRect().bottom : NaN;
      var bar = document.querySelector('.le-mtopbar');
      var tabs = document.querySelector('.le-tabs');
      box.textContent =
        'layoutH ' + document.documentElement.clientHeight + '  innerH ' + window.innerHeight + '\n' +
        'vv h ' + Math.round(vv.height) + '  offTop ' + Math.round(vv.offsetTop) +
        '  pageTop ' + Math.round(vv.pageTop) + '  scale ' + (Math.round(vv.scale * 100) / 100) + '\n' +
        'probeTop.top ' + Math.round(pt) + '   probeBot.bottom ' + Math.round(pb) + '\n' +
        'CORRECTION top ' + appliedT + '  bot ' + appliedB +
        '   dvh ' + (window.CSS && CSS.supports && CSS.supports('height', '100dvh') ? 'yes' : 'NO') + '\n' +
        'topbar.top ' + (bar ? Math.round(bar.getBoundingClientRect().top) : '-') +
        '   tabs.bottom ' + (tabs ? Math.round(tabs.getBoundingClientRect().bottom) : '-') +
        '   scrollY ' + Math.round(window.scrollY);
    }
    function start() {
      if (!document.body) return;
      document.body.appendChild(box);
      setInterval(draw, 150);
      draw();
    }
    if (document.body) start();
    else document.addEventListener('DOMContentLoaded', start);
  })();

  /* ---------- publish the TOP BAR's real height ----------
     Anything that docks under .le-mtopbar needs its height, and deriving it
     from the CSS is guesswork: the padding says 52px, but the bar is sized by
     its tallest child — the search field, not the logo — so it actually
     measures 59. Two places had quietly hardcoded the wrong number and tucked
     7px of themselves underneath it.

     This is now a REFINEMENT, not the definition. --le-mtopbar-h is declared
     at :root in le-mobile.css as `59px + env(safe-area-inset-top)`, because
     <body> uses it as the spacer for the bar being out of flow and a spacer
     that only exists once a script has run is a spacer that is wrong for the
     first paint — that is what sank the previous attempt at a fixed top bar.
     This measurement lands on the same number and keeps it honest if the bar's
     padding or contents ever change. */
  function syncTopbarHeight() {
    var bar = document.querySelector('.le-mtopbar');
    if (!bar) return;
    var h = bar.getBoundingClientRect().height;
    if (!h) return;
    document.documentElement.style.setProperty('--le-mtopbar-h', Math.round(h) + 'px');
  }
  syncTopbarHeight();
  window.addEventListener('load', syncTopbarHeight);
  window.addEventListener('resize', syncTopbarHeight);
  window.addEventListener('orientationchange', syncTopbarHeight);


  /* ---------------------------------------------------------
     1. Accordions.
     Liquid renders every <details class="le-macc"> with `open`, so
     crawlers and no-JS visitors get the full page expanded. Here we
     close them on phones only. The FIRST one stays open so the panel
     never reads as an empty stack of bars.
     --------------------------------------------------------- */
  var mq = window.matchMedia(MOBILE);

  function applyAccordions(isMobile) {
    var groups = {};
    document.querySelectorAll('details.le-macc').forEach(function (d) {
      var key = d.closest('[data-le-acc-group]');
      key = key ? key.getAttribute('data-le-acc-group') : '_';
      (groups[key] = groups[key] || []).push(d);
    });
    Object.keys(groups).forEach(function (k) {
      groups[k].forEach(function (d, i) {
        // Desktop: everything open (matches the pre-existing flat layout).
        // Mobile: first open, rest collapsed.
        d.open = isMobile ? i === 0 : true;
      });
    });
  }

  applyAccordions(mq.matches);
  // addEventListener on MediaQueryList is Safari 14+; the old API is the
  // fallback so this does not throw on older iOS.
  if (mq.addEventListener) mq.addEventListener('change', function (e) { applyAccordions(e.matches); });
  else if (mq.addListener) mq.addListener(function (e) { applyAccordions(e.matches); });

  // One open at a time within a group, on mobile only.
  document.addEventListener('click', function (e) {
    var sum = e.target.closest('details.le-macc > summary');
    if (!sum || !mq.matches) return;
    var d = sum.parentNode;
    if (d.open) return; // it is about to close; nothing to collapse
    var scope = d.closest('[data-le-acc-group]') || document;
    scope.querySelectorAll('details.le-macc').forEach(function (o) {
      if (o !== d && o.closest('[data-le-acc-group]') === d.closest('[data-le-acc-group]')) o.open = false;
    });
  });

  /* ---------------------------------------------------------
     2. Cart badge.
     Reads the count off the same 'cart:update' event le-cart-add.js
     already dispatches, so there is no second source of truth and no
     extra request.
     --------------------------------------------------------- */
  function setCount(n) {
    document.querySelectorAll('[data-le-cart-count]').forEach(function (b) {
      var prev = parseInt(b.textContent, 10) || 0;
      b.textContent = n;
      b.hidden = n <= 0;
      if (n > prev) {
        b.classList.remove('is-bumped');
        void b.offsetWidth; // restart the animation
        b.classList.add('is-bumped');
      }
    });
  }

  // le-cart-add.js dispatches cart:update with { detail: { resource: <cart> } }
  // (Horizon's own CartAddEvent shape). Other emitters in the theme may use
  // detail.cart, so both are accepted rather than assuming one.
  document.addEventListener('cart:update', function (e) {
    // The section re-render can restore the auto-open attribute; re-strip it.
    syncDrawerAutoOpen();
    setTimeout(releaseStuckDrawer, 60);
    var d = (e && e.detail) || {};
    var c = d.resource || d.cart || null;
    if (!c || typeof c.item_count !== 'number') return;
    // Shiptection protection is an add-on, not an item (le-shiptection.liquid).
    var n = c.item_count;
    (c.items || []).forEach(function (it) {
      if (it.handle === 'shiptection-shipping-protection') n -= it.quantity;
    });
    setCount(n);
  });

  /* ---------------------------------------------------------
     2b. Stop Horizon auto-opening the cart drawer on mobile.

     THE FREEZE. settings.auto_open_cart_drawer is TRUE on this store, which
     puts an `auto-open` attribute on <cart-drawer-component>. So after an add
     Horizon opened the drawer itself — regardless of le-cart-add.js choosing
     not to. Its showDialog() pins `body { position:fixed; top:-Ypx }`, and the
     drawer lives INSIDE .le-navbar-section, which the mobile layout hides
     wholesale. Result: the page was locked by a dialog nobody could see or
     close, and le-sheet-lock's watchdog correctly refused to intervene because
     a dialog genuinely WAS open.

     Removing the attribute on mobile is the narrow fix: no auto-open, no
     dialog, no lock. Desktop keeps its drawer untouched. The attribute is
     restored if the viewport grows, and re-checked after every cart:update
     because the section re-render can put it back.
     --------------------------------------------------------- */
  function syncDrawerAutoOpen() {
    var d = document.querySelector('cart-drawer-component');
    if (!d) return;
    if (mq.matches) {
      if (d.hasAttribute('auto-open')) {
        d.__leHadAutoOpen = true;
        d.removeAttribute('auto-open');
      }
    } else if (d.__leHadAutoOpen && !d.hasAttribute('auto-open')) {
      d.setAttribute('auto-open', '');
    }
  }

  syncDrawerAutoOpen();
  if (mq.addEventListener) mq.addEventListener('change', syncDrawerAutoOpen);
  else if (mq.addListener) mq.addListener(syncDrawerAutoOpen);

  /* Belt and braces: if anything still manages to open the drawer on mobile,
     close it and release the body pin rather than leaving the page stuck. */
  function releaseStuckDrawer() {
    if (!mq.matches) return;
    var dlg = document.querySelector('cart-drawer-component dialog[open]');
    if (!dlg) return;
    try { dlg.close(); } catch (err) { /* ignore */ }
    var b = document.body;
    if (b.style.position === 'fixed') {
      var y = Math.abs(parseFloat(b.style.top || '0')) || 0;
      b.style.width = ''; b.style.position = ''; b.style.top = '';
      window.scrollTo(0, y);
    }
  }

  /* ---------------------------------------------------------
     3. Toast.
     Replaces the cart drawer on mobile. le-cart-add.js fires
     'le:cart-added' after a successful add; the drawer is suppressed
     for that same add. Auto-dismisses; tapping anywhere on it that is
     not the CTA closes it early.
     --------------------------------------------------------- */
  var toastTimer = null;

  function showToast(msg) {
    var t = document.querySelector('[data-le-toast]');
    if (!t) return;
    var m = t.querySelector('[data-le-toast-msg]');
    if (m && msg) m.textContent = msg;
    t.hidden = false;

    // Two frames so the transition has a from-state to animate out of — but
    // rAF NEVER FIRES when the frame loop is starved (backgrounded tab, iOS
    // low-power mode, a hidden webview). That left the toast unhidden but
    // parked at translateY(-140%): no confirmation at all, which on mobile is
    // the only feedback an add gets now that the drawer no longer opens.
    // So the same work is raced from a timeout and whichever wins runs once.
    var armed = false;
    var arm = function () {
      if (armed) return;
      armed = true;
      t.classList.add('is-in');
    };
    requestAnimationFrame(function () { requestAnimationFrame(arm); });
    setTimeout(arm, 80);

    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 3200);
  }

  function hideToast() {
    var t = document.querySelector('[data-le-toast]');
    if (!t) return;
    t.classList.remove('is-in');
    clearTimeout(toastTimer);
    setTimeout(function () { if (!t.classList.contains('is-in')) t.hidden = true; }, 320);
  }

  document.addEventListener('le:cart-added', function (e) {
    if (!mq.matches) return; // desktop keeps the native drawer
    var d = (e && e.detail) || {};
    showToast(d.title ? 'Added — ' + d.title : 'Added to cart');
  });

  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-le-toast]');
    if (t && !e.target.closest('.le-toast__cta')) hideToast();
  });

  /* ---------------------------------------------------------
     4. Menu drawer.
     This IS a modal that covers the page, so it uses the shared
     ref-counted LESheetLock — per CLAUDE.md, every modal sheet must.
     (The DESKTOP mega-menu deliberately does not lock; it is not a
     modal. Do not "make them consistent".)
     --------------------------------------------------------- */
  var menuOpen = false;

  function openMenu() {
    var m = document.getElementById('le-mobile-menu');
    if (!m || menuOpen) return;
    menuOpen = true;
    m.hidden = false;
    if (window.LESheetLock && LESheetLock.lock) LESheetLock.lock();
    // Same rAF-plus-timeout race as the toast: a starved frame loop would
    // otherwise leave the panel parked off-screen with the scrim up.
    var armed = false;
    var arm = function () { if (armed) return; armed = true; m.classList.add('is-in'); };
    requestAnimationFrame(function () { requestAnimationFrame(arm); });
    setTimeout(arm, 80);
    var btn = document.querySelector('[data-le-menu-open]');
    if (btn) btn.setAttribute('aria-expanded', 'true');
    var x = m.querySelector('.le-menu__x');
    if (x) x.focus();
  }

  function closeMenu() {
    var m = document.getElementById('le-mobile-menu');
    if (!m || !menuOpen) return;
    menuOpen = false;
    m.classList.remove('is-in');
    if (window.LESheetLock && LESheetLock.unlock) LESheetLock.unlock();
    var btn = document.querySelector('[data-le-menu-open]');
    if (btn) btn.setAttribute('aria-expanded', 'false');
    setTimeout(function () { if (!menuOpen) m.hidden = true; }, 340);
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-le-menu-open]')) { e.preventDefault(); openMenu(); return; }
    if (e.target.closest('[data-le-menu-close]')) { e.preventDefault(); closeMenu(); return; }
    // A link inside the drawer navigates; drop the lock first so the next
    // page never inherits a locked <html> if the browser restores from bfcache.
    if (menuOpen && e.target.closest('.le-menu__panel a[href]')) closeMenu();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && menuOpen) closeMenu();
  });

  /* ---------------------------------------------------------
     4b. Top-bar search.
     The bar's "field" is a button. Tapping it opens Horizon's OWN
     search modal via the same call its search-action button makes
     (snippets/search.liquid: on:click="#search-modal/showDialog"),
     so predictive search, recently-viewed and the blurred scrim all
     come along and there is no second search to keep in sync.
     --------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    if (!e.target.closest('[data-le-search]')) return;
    e.preventDefault();
    if (menuOpen) closeMenu();
    var modal = document.getElementById('search-modal');
    if (modal && typeof modal.showDialog === 'function') { modal.showDialog(); return; }
    // Fallback: click Horizon's own trigger, wherever it is in the header.
    var btn = document.querySelector('search-button button, .search-action button');
    if (btn) { btn.click(); return; }
    location.href = '/search';
  });

  /* ---------------------------------------------------------
     4c. Review-track advance buttons (mobile).

     The reviews section already scrolls horizontally; it just had no way to
     advance by tap. The buttons are CHROME, so injecting them from JS is fine —
     no review content is created here, and the cards stay server-rendered.

     Scrolls by one card width. Uses a direct scrollLeft assignment inside a
     rAF tween for the same reason the PDP thumb rail does: smooth scrolling is
     a no-op in some engines, and a timeout lands the final position when the
     frame loop is starved.
     --------------------------------------------------------- */
  function buildReviewNav() {
    if (!mq.matches) return;
    var wrap = document.querySelector('.le-reviews .le-rv-wrap');
    if (!wrap || wrap.querySelector('.le-rv-nav')) return;
    var track = wrap.querySelector('.le-rv-track:not([hidden])');
    if (!track || track.children.length < 2) return;

    var nav = document.createElement('div');
    nav.className = 'le-rv-nav';
    nav.innerHTML =
      '<button type="button" data-rv="-1" aria-label="Previous reviews"><svg viewBox="0 0 24 24"><path d="M15 5l-7 7 7 7"/></svg></button>' +
      '<button type="button" data-rv="1" aria-label="More reviews"><svg viewBox="0 0 24 24"><path d="M9 5l7 7-7 7"/></svg></button>';
    wrap.appendChild(nav);

    function tween(el, target) {
      var max = el.scrollWidth - el.clientWidth;
      if (target < 0) target = 0;
      if (target > max) target = max;
      if (el.__leTween) cancelAnimationFrame(el.__leTween);
      clearTimeout(el.__leTweenSafety);
      var from = el.scrollLeft, dist = target - from, start = null, done = false;
      var settle = function () { if (done) return; done = true; el.scrollLeft = target; sync(); };
      var step = function (ts) {
        if (done) return;
        if (start === null) start = ts;
        var t = Math.min(1, (ts - start) / 300);
        el.scrollLeft = from + dist * (1 - Math.pow(1 - t, 3));
        if (t < 1) el.__leTween = requestAnimationFrame(step); else settle();
      };
      el.__leTween = requestAnimationFrame(step);
      el.__leTweenSafety = setTimeout(settle, 420);
    }

    function activeTrack() { return wrap.querySelector('.le-rv-track:not([hidden])'); }
    function sync() {
      var t = activeTrack(); if (!t) return;
      var max = t.scrollWidth - t.clientWidth;
      nav.querySelector('[data-rv="-1"]').disabled = t.scrollLeft <= 2;
      nav.querySelector('[data-rv="1"]').disabled = t.scrollLeft >= max - 2;
    }

    nav.addEventListener('click', function (e) {
      var b = e.target.closest('[data-rv]'); if (!b) return;
      var t = activeTrack(); if (!t) return;
      var card = t.children[0];
      var stepPx = card ? card.getBoundingClientRect().width + 12 : t.clientWidth * 0.85;
      tween(t, t.scrollLeft + stepPx * parseInt(b.getAttribute('data-rv'), 10));
    });
    wrap.addEventListener('scroll', sync, true);
    sync();
  }

  // Reviews arrive asynchronously, so wait for the track rather than racing it.
  (function waitForReviews(tries) {
    if (tries > 40) return;
    var t = document.querySelector('.le-reviews .le-rv-track');
    if (t && t.children.length) { buildReviewNav(); return; }
    setTimeout(function () { waitForReviews(tries + 1); }, 250);
  })(0);

  /* ---------------------------------------------------------
     4d. Page order on mobile: ... reviews -> More information -> related.

     The related rails are rendered inside the mobile PDP section, so they sit
     ABOVE the reviews and More Information sections that follow it. Sections
     are siblings and CSS cannot reorder across them, so the rails are MOVED to
     the end of the page instead.

     This is a DOM move of markup that is already server-rendered — every card
     is in the initial HTML either way, so nothing changes for a crawler; only
     the reading order does. Desktop is untouched.
     --------------------------------------------------------- */
  function orderMobileSections() {
    if (!mq.matches) return;
    var reviews = document.querySelector('.le-reviews');
    var more    = document.querySelector('.le-pdp-acc');
    var help    = document.querySelector('.le-pdp-help');
    var head    = document.querySelector('[data-rel-head]');
    var rails   = Array.prototype.slice.call(document.querySelectorAll('[data-rel]'));
    if (!reviews || !head || !rails.length) return;

    var anchor = reviews.closest('.shopify-section') || reviews;

    // 1. More information ABOVE the reviews.
    if (more && more.nextElementSibling !== anchor) {
      anchor.parentNode.insertBefore(more, anchor);
    }
    // 2. You may also like AFTER the reviews.
    var prev = anchor;
    anchor.parentNode.insertBefore(head, prev.nextSibling);
    prev = head;
    rails.forEach(function (r) { prev.parentNode.insertBefore(r, prev.nextSibling); prev = r; });
    // 3. Need help choosing LAST.
    if (help) prev.parentNode.insertBefore(help, prev.nextSibling);
  }

  (function waitForOrder(t) {
    if (t > 40) return;
    if (document.querySelector('.le-pdp-acc') || document.querySelector('.le-reviews')) { orderMobileSections(); return; }
    setTimeout(function () { waitForOrder(t + 1); }, 250);
  })(0);

  /* ---------------------------------------------------------
     5. Shopify Inbox chat.
     The floating "Chat" bubble sits bottom-left and lands on top of
     product cards and the tab bar. It is now replaced by a real control at
     BOTH ends: the menu's Support row on phones, and the gold Chat button in
     the navbar on desktop. Both carry [data-le-chat] and both come through the
     one handler at the bottom of this block, so there is a single chat
     integration on the site rather than one per breakpoint.

     Between 768px and 960px there is neither — the navbar has collapsed to the
     hamburger and the labelled button does not fit, but the mobile menu is not
     in play either. The floating bubble is deliberately LEFT ALONE in that
     band, so no width is left with no way to reach chat.

     Structure, established by inspecting the live widget:
       <shopify-chat open view="chat">        <- host, open shadow root
         #shadow-root
           <shopify-agent-runtime exportparts="activator, dialog">
             #shadow-root  -> [part=activator], [part=dialog], [part=iframe]

     Two things fall out of that, and both are sturdier than the obvious
     approach of clicking a button by class name:

     HIDING — shopify-chat does NOT carry `exportparts`, so ::part() cannot
     reach the activator from document CSS. But INSIDE the host's shadow
     root, shopify-agent-runtime::part(activator) does resolve. A part name
     is a published contract; a class name is not. We inject there, and keep
     the class selector alongside it purely as a version fallback.

     OPENING — `open` is a plain attribute on the HOST and setting it alone
     opens the panel (verified: no click required). That is far better than
     dispatching a synthetic click at a button we deliberately hid.

     Hidden with opacity/pointer-events rather than display:none so the
     element keeps its box and the panel still positions correctly.

     Still unsupported API surface, so the menu row stays a REAL <a href> to
     the contact page: every failure path below falls through to it.
     --------------------------------------------------------- */
  var CHAT_HIDE_ID = 'le-chat-hide';
  /* 961px matches the navbar's own collapse point in lab-effects-navbar.css —
     above it the gold Chat button is in the action row, below it that row goes
     to compact 38px tiles and the button is hidden. Keep the two in step. */
  var chatBtnMq = window.matchMedia('(min-width: 961px)');
  function chatReplaced() { return mq.matches || chatBtnMq.matches; }

  function chatHost() { return document.querySelector('shopify-chat'); }

  /* Reveal the host once we have either hidden its bubble or decided not to.
     Until this runs the host is visibility:hidden (see le-mobile.css), which is
     what stops the bubble flashing in and being pulled away on every page. */
  function markChatReady() {
    var h = chatHost();
    if (h) h.classList.add('le-chat-ready');
  }
  // Failsafe: never leave the widget invisible because our own code did not run.
  setTimeout(markChatReady, 3000);

  function hideChatBubble() {
    if (!chatReplaced()) { markChatReady(); return; }
    var h = chatHost();
    if (!h || !h.shadowRoot) return;
    if (h.shadowRoot.getElementById(CHAT_HIDE_ID)) return;
    try {
      var st = document.createElement('style');
      st.id = CHAT_HIDE_ID;
      st.textContent =
        'shopify-agent-runtime::part(activator),button.activator{' +
        'opacity:0!important;pointer-events:none!important;transform:translateY(250%)!important;}';
      h.shadowRoot.appendChild(st);
    } catch (err) { /* leave the bubble visible rather than break the widget */ }
    markChatReady();
  }

  function showChatBubble() {
    var h = chatHost();
    if (!h || !h.shadowRoot) return;
    var st = h.shadowRoot.getElementById(CHAT_HIDE_ID);
    if (st) st.remove();
    markChatReady();
  }

  function openChat() {
    var h = chatHost();
    if (!h) return false;
    try {
      h.setAttribute('open', '');
      return true;
    } catch (err) { /* fall through to the click path */ }
    try {
      var act = h.shadowRoot && h.shadowRoot.querySelector('button.activator');
      if (act) { act.click(); return true; }
    } catch (err2) { /* fall through to the href */ }
    return false;
  }

  // The element upgrades after the app script loads, so wait for it rather
  // than racing it. Bounded retries — never an unbounded poll.
  (function waitForChat(tries) {
    if (tries > 60) { markChatReady(); return; }   // give up, but never leave it hidden
    var h = chatHost();
    if (h && h.shadowRoot) { hideChatBubble(); return; }
    /* Tight polling for the first ~600ms, then back off. The widget usually
       upgrades inside that window, and every millisecond before it does is a
       millisecond the host is sitting invisible. */
    setTimeout(function () { waitForChat(tries + 1); }, tries < 20 ? 30 : 250);
  })(0);

  function syncChatBubble() { chatReplaced() ? hideChatBubble() : showChatBubble(); }
  [mq, chatBtnMq].forEach(function (q) {
    if (q.addEventListener) q.addEventListener('change', syncChatBubble);
    else if (q.addListener) q.addListener(syncChatBubble);
  });

  document.addEventListener('click', function (e) {
    var row = e.target.closest('[data-le-chat]');
    if (!row) return;
    if (!chatHost()) return;             // app off — let the <a href> go to /pages/contact
    e.preventDefault();
    /* Only the mobile menu needs closing, and only then is the delay wanted:
       the drawer has to finish closing and release the scroll lock before the
       panel mounts, or it opens against a locked <html>. From the desktop
       navbar button there is no drawer and no lock, so waiting 360ms would be
       360ms of nothing happening after a click. */
    var wasMenuOpen = menuOpen;
    if (wasMenuOpen) closeMenu();
    var go = function () {
      if (!openChat()) location.href = row.getAttribute('href') || '/pages/contact';
    };
    if (wasMenuOpen) setTimeout(go, 360); else go();
  });

  /* ---------------------------------------------------------
     TAB BAR TAP FEEDBACK

     A class rather than :active — on iOS :active does not reliably fire for a
     quick tap unless the element has its own touch handler, and it sticks on
     when a scroll gesture begins on the bar. touchstart/touchend track the
     finger exactly, and touchcancel covers the scroll-away case.

     HAPTICS: navigator.vibrate() where it exists. That is Android; iOS Safari
     and Chrome on iOS both run WebKit, which does not implement the Vibration
     API at all, so there is nothing to call there — this is a real platform
     gap, not a missing feature flag. The visual wash is what every device gets.
     --------------------------------------------------------- */
  var TAP = 'is-tapped';
  /* MOBILE ONLY. The tab bar is display:none above 767px, so there is nothing
     to light up there and no reason to run these listeners. */
  var tapMq = window.matchMedia('(max-width: 767px)');
  /* The filter chips light up too. They need the same treatment for the same
     reason — iOS will not give :active to an element on touch unless the
     document is already listening — and the Application chips need it most,
     because they are links: the tap navigates, and with no press state the
     sheet just sits there and the tap reads as a miss. */
  var TAP_SEL = '.le-tab, .lab-col-filterpanel .lab-col-chip';
  function tapOn(e) {
    if (!tapMq.matches) return;
    var t = e.target.closest(TAP_SEL);
    if (!t) return;
    t.classList.add(TAP);
    // Haptics stay on the tab bar alone. A tick per filter chip would fire a
    // dozen times while someone stacks a filter set, which is a buzz, not
    // feedback. 8ms is a tick — the weight of a key press.
    if (t.classList.contains('le-tab') && navigator.vibrate) {
      try { navigator.vibrate(8); } catch (err) {}
    }
  }
  function tapOff(e) {
    var t = e.target.closest(TAP_SEL);
    if (t) t.classList.remove(TAP);
    // A finger can leave the element it started on, so sweep any stragglers.
    var stuck = document.querySelectorAll('.' + TAP);
    for (var i = 0; i < stuck.length; i++) stuck[i].classList.remove(TAP);
  }
  document.addEventListener('touchstart', tapOn, { passive: true });
  document.addEventListener('touchend', tapOff, { passive: true });
  document.addEventListener('touchcancel', tapOff, { passive: true });
  /* Pointer events for anything without touch (a trackpad in responsive mode,
     a stylus), so the bar is not dead-feeling there either. */
  document.addEventListener('pointerdown', function (e) {
    if (e.pointerType !== 'touch') tapOn(e);
  });
  document.addEventListener('pointerup', function (e) {
    if (e.pointerType !== 'touch') tapOff(e);
  });

  // bfcache restore: the drawer must never come back open with a stale lock.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted && menuOpen) {
      menuOpen = false;
      var m = document.getElementById('le-mobile-menu');
      if (m) { m.classList.remove('is-in'); m.hidden = true; }
      if (window.LESheetLock && LESheetLock.reset) LESheetLock.reset();
    }
  });
})();

/* ============================================================================
   VIEWPORT PROBE — inert unless the URL carries ?ledebug=1

   The top bar has now been "fixed" twice on a theory and broken both times, on
   Chrome iOS specifically. This stops the guessing: it prints the numbers that
   actually decide where the bars land, live, on the real device, while you
   scroll. Safari and Chrome iOS report these differently, which is the whole
   problem — env(safe-area-inset-top) is the NOTCH, not the browser toolbar, so
   nothing in CSS currently knows how tall Chrome's top chrome is.

   Read it, screenshot it at rest and mid-scroll, and the fix stops being a
   guess. Delete this block once the top bar is settled.
============================================================================ */
(function leViewportProbe() {
  try {
    if (!/[?&]ledebug=1/.test(location.search)) return;
    var box = document.createElement('div');
    box.style.cssText = 'position:fixed;left:6px;right:6px;top:50%;transform:translateY(-50%);' +
      'z-index:2147483647;background:rgba(4,17,19,.94);color:#9bc68e;font:600 11px/1.45 ui-monospace,Menlo,monospace;' +
      'padding:10px 12px;border-radius:10px;border:1px solid #9bc68e;white-space:pre;pointer-events:none';
    document.body.appendChild(box);

    var probe = document.createElement('div');
    probe.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;' +
      'padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px)';
    document.body.appendChild(probe);

    function n(v) { return Math.round(v); }
    function draw() {
      var cs = getComputedStyle(probe);
      var vv = window.visualViewport;
      var tb = document.querySelector('.le-mtopbar');
      var tabs = document.querySelector('.le-tabs');
      var tr = tb ? tb.getBoundingClientRect() : null;
      var br = tabs ? tabs.getBoundingClientRect() : null;
      box.textContent =
        'scrollY        ' + n(window.scrollY) + '\n' +
        'innerHeight    ' + n(window.innerHeight) + '\n' +
        'clientHeight   ' + n(document.documentElement.clientHeight) + '\n' +
        (vv ? 'visualVP h/off ' + n(vv.height) + ' / ' + n(vv.offsetTop) + '  pageTop ' + n(vv.pageTop) + '\n' : 'visualVP       n/a\n') +
        'safe-top/bot   ' + cs.paddingTop + ' / ' + cs.paddingBottom + '\n' +
        (tr ? 'topbar  top/h  ' + n(tr.top) + ' / ' + n(tr.height) + '\n' : '') +
        (br ? 'tabbar  top/bo ' + n(br.top) + ' / ' + n(br.bottom) + '\n' : '') +
        'UA             ' + (/CriOS/.test(navigator.userAgent) ? 'Chrome iOS' : /Safari/.test(navigator.userAgent) ? 'Safari iOS' : 'other');
    }
    draw();
    addEventListener('scroll', draw, { passive: true });
    addEventListener('resize', draw);
    if (window.visualViewport) {
      visualViewport.addEventListener('resize', draw);
      visualViewport.addEventListener('scroll', draw);
    }
  } catch (e) {}
})();

